import {
  DEFAULT_CREATE_FORM,
  createDefaultWorkflowForm,
  create_workflow_form_from_sla,
  get_first_workflow_assignment,
  get_unassign_workflow_context,
  get_workflow_assignment_context,
  normalize_positive_id,
  run_request_with_loader,
  submit_sla_config,
  submit_workflow_assignment,
} from './service_layer_agreement.helpers';
import {
  build_create_payload_from_form,
  build_update_form_data,
  get_create_form_validation_message,
} from './service_layer_agreement.form_helpers';
import { fetch_sla_assigned_workflows, remove_sla_workflow_binding } from '@/services/workflow_service';

export default {
  open_create_modal() {
    this.create_form = DEFAULT_CREATE_FORM();
  },
  open_update_modal(sla = {}) {
    const update_form = build_update_form_data(sla, this.format_duration);
    this.create_form = update_form;
  },
  open_workflow_modal(sla = {}) {
    this.workflow_form = create_workflow_form_from_sla(sla);
    if (this.workflow_form.sla_config_id) {
      this.refresh_workflow_modal();
    }
  },
  async refresh_workflow_modal() {
    const assigned_workflows = await fetch_sla_assigned_workflows(this.workflow_form.sla_config_id)
      .catch((error) => {
        this.show_error(error, 'Failed to fetch SLA assigned workflows');
        return [];
      });
    const current_workflow = get_first_workflow_assignment(assigned_workflows);

    if (current_workflow) {
      this.apply_current_workflow_to_form(current_workflow);
    }
  },
  apply_current_workflow_to_form(current_workflow = {}) {
    this.workflow_form = {
      ...this.workflow_form,
      current_workflow_id: current_workflow.workflow_id || null,
      current_workflow_name: current_workflow.workflow_name || '',
      workflow_id: current_workflow.workflow_id ? String(current_workflow.workflow_id) : this.workflow_form.workflow_id,
    };
  },
  close_workflow_modal() {
    this.workflow_form = createDefaultWorkflowForm();
  },
  close_create_modal() {
    this.create_form = DEFAULT_CREATE_FORM();
  },
  validate_create_form() {
    const validation_message = get_create_form_validation_message(this.create_form, this.parse_duration_input);
    if (!validation_message) return true;
    this.show_error(validation_message);
    return false;
  },
  normalize_workflow_id(value) {
    const workflow_id = normalize_positive_id(value);
    return workflow_id;
  },
  build_create_payload() {
    const payload = build_create_payload_from_form(this.create_form, this.parse_duration_input);
    return payload;
  },
  async unassign_workflow_from_sla() {
    const context = get_unassign_workflow_context(this.workflow_form.sla_config_id, this.workflow_form.current_workflow_id);
    if (context.error) {
      this.show_error(context.error);
      return false;
    }

    return run_request_with_loader(this, {
      lock_key: 'workflow_saving',
      loader_message: 'Removing workflow assignment...',
      failure_message: 'Failed to unassign workflow.',
      request: () => remove_sla_workflow_binding({
        sla_config_id: context.sla_config_id,
        workflow_id: context.workflow_id,
      }),
      on_success: async () => {
        await this.fetch_sla_configs();
        this.close_modal('workflow_assignment_modal');
        this.close_workflow_modal();
        this.show_success('Workflow unassigned successfully.');
      },
    });
  },
  async save_workflow_assignment() {
    const context = get_workflow_assignment_context(
      this.workflow_form.sla_config_id,
      this.workflow_form.workflow_id,
      this.workflow_form.current_workflow_id,
    );

    if (context.error) {
      this.show_error(context.error);
      return false;
    }

    if (context.current_workflow_id && context.current_workflow_id === context.workflow_id) {
      this.close_modal('workflow_assignment_modal');
      this.close_workflow_modal();
      return true;
    }

    return run_request_with_loader(this, {
      lock_key: 'workflow_saving',
      loader_message: 'Saving workflow assignment...',
      failure_message: 'Failed to assign workflow.',
      request: () => submit_workflow_assignment(context),
      on_success: async () => {
        await this.fetch_sla_configs();
        this.close_modal('workflow_assignment_modal');
        this.close_workflow_modal();
        this.show_success('Workflow assigned successfully.');
      },
    });
  },
  async save_sla_config(is_update = false) {
    if (!this.validate_create_form()) {
      return false;
    }

    return run_request_with_loader(this, {
      lock_key: 'saving',
      loader_message: is_update ? 'Updating SLA...' : 'Creating SLA...',
      failure_message: is_update ? 'Failed to update SLA.' : 'Failed to create SLA.',
      request: () => submit_sla_config(this, is_update),
      on_success: async (response_data) => {
        await this.fetch_sla_configs();
        if (is_update) {
          this.close_modal('update_sla_modal');
        } else {
          this.close_modal('create_sla_modal');
        }
        this.close_create_modal();
        this.show_success(response_data?.message || (is_update ? 'SLA updated successfully.' : 'SLA created successfully.'));
      },
    });
  },
  async create_sla_config() {
    await this.save_sla_config(false);
  },
  async update_sla_config() {
    await this.save_sla_config(true);
  },
  go_to_manage_sla(sla) {
    if (!sla?.id) return;
    this.$router.push({
      name: 'ServiceLayerTrigger',
      query: { sla_id: String(sla.id) },
    });
  },
  manage_workflow(sla) {
    if (!sla?.id) return;
    this.open_workflow_modal(sla);
  },
};
